import axios, { AxiosError } from 'axios';
import { 
  AppIcon, 
  SearchParams, 
  SearchResponse, 
  AIEditParams, 
  AIEditResponse,
  CountryCode,
  ApiResponse
} from '../types';
import { getVolcengineApiKey, getSeedreamModelId } from '../config';

// API基础配置
const API_CONFIG = {
  ITUNES_BASE_URL: 'https://itunes.apple.com',
  ARK_BASE_URL: 'https://ark.cn-beijing.volces.com',
  TIMEOUT: 20000,
  RETRY_COUNT: 3,
  RETRY_DELAY: 1000,
};

// 创建axios实例
const itunesApi = axios.create({
  baseURL: API_CONFIG.ITUNES_BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

const volcengineApi = axios.create({
  baseURL: '/ark',
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
itunesApi.interceptors.request.use(
  (config) => {
    console.log(`[iTunes API] ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => Promise.reject(error)
);

volcengineApi.interceptors.request.use(
  (config) => {
    console.log(`[Volcengine API] ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器
itunesApi.interceptors.response.use(
  (response) => {
    console.log(`[iTunes API] Response:`, response.data);
    return response;
  },
  (error: AxiosError) => {
    console.error(`[iTunes API] Error:`, error.message);
    return Promise.reject(error);
  }
);

volcengineApi.interceptors.response.use(
  (response) => {
    console.log(`[Volcengine API] Response:`, response.data);
    return response;
  },
  (error: AxiosError) => {
    console.error(`[Volcengine API] Error:`, error.message);
    return Promise.reject(error);
  }
);

/**
 * iTunes图标搜索服务
 */
export class iTunesService {
  /**
   * 搜索应用图标
   */
  static async searchIcons(params: SearchParams): Promise<ApiResponse<SearchResponse>> {
    try {
      const searchParams = new URLSearchParams({
        term: params.term,
        entity: params.entity || 'software',
        limit: (params.limit || 50).toString(),
        offset: (params.offset || 0).toString(),
      });

      // 根据国家代码设置区域参数
      const country = params.country || 'us';
      const countryCode = this.getCountryCode(country);
      
      const response = await itunesApi.get(`/search?${searchParams.toString()}&country=${countryCode}`);
      
      // 转换响应数据格式，限制结果最多25个
      const results = response.data.results.slice(0, 25).map((item: any) => ({
        trackId: item.trackId,
        trackName: item.trackName,
        artworkUrl512: item.artworkUrl512 || item.artworkUrl100 || item.artworkUrl60,
        artworkUrl256: item.artworkUrl256 || item.artworkUrl100,
        artworkUrl128: item.artworkUrl60,
        artistName: item.artistName,
        description: item.description,
        country: country,
        primaryGenreName: item.primaryGenreName,
        averageUserRating: item.averageUserRating,
        userRatingCount: item.userRatingCount,
        price: item.price,
        formattedPrice: item.formattedPrice,
      }));

      return {
        success: true,
        data: {
          resultCount: response.data.resultCount,
          results: results,
        },
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      return {
        success: false,
        error: this.getErrorMessage(axiosError),
      };
    }
  }

  /**
   * 获取热门应用图标（基于排行榜）
   */
  static async getTopIcons(country: CountryCode, limit: number = 20): Promise<ApiResponse<SearchResponse>> {
    try {
      const countryCode = this.getCountryCode(country);
      const genres = ['6013', '6000', '6001', '6002', '6003']; // 各种应用类别
      
      // 获取多个类别的热门应用
      const promises = genres.map(genre => 
        itunesApi.get(`/rss/topfreeapplications/limit=${Math.ceil(limit / genres.length)}/genre=${genre}/json`)
          .catch(() => ({ data: { feed: { entry: [] } } }))
      );

      const responses = await Promise.allSettled(promises);
      const allApps: any[] = [];

      responses.forEach((response, index) => {
        if (response.status === 'fulfilled' && response.value.data?.feed?.entry) {
          const apps = response.value.data.feed.entry.map((item: any) => ({
            trackId: item.id.attributes['im:id'],
            trackName: item['im:name'].label,
            artworkUrl512: item['im:image'][2].label, // 512x512
            artworkUrl256: item['im:image'][1].label, // 256x256
            artworkUrl128: item['im:image'][0].label, // 128x128
            artistName: item['im:artist'].label,
            description: item.summary?.label,
            country: country,
            primaryGenreName: item.category?.attributes?.term,
            averageUserRating: item['im:rating']?.label ? parseFloat(item['im:rating'].label) : undefined,
            userRatingCount: item['im:rating-count']?.label ? parseInt(item['im:rating-count'].label) : undefined,
            price: item['im:price']?.label ? parseFloat(item['im:price'].label.replace(/[^\d.]/g, '')) : 0,
            formattedPrice: item['im:price']?.label || '免费',
          }));
          allApps.push(...apps);
        }
      });

      // 去重并排序
      const uniqueApps = allApps.filter((app, index, self) => 
        index === self.findIndex(a => a.trackId === app.trackId)
      ).slice(0, limit);

      return {
        success: true,
        data: {
          resultCount: uniqueApps.length,
          results: uniqueApps,
        },
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      return {
        success: false,
        error: this.getErrorMessage(axiosError),
      };
    }
  }

  /**
   * 根据国家代码获取iTunes国家代码
   */
  private static getCountryCode(country: CountryCode | string): string {
    const countryMap: Record<CountryCode, string> = {
      cn: 'cn',
      us: 'us',
      jp: 'jp',
      kr: 'kr',
    };
    return countryMap[country as CountryCode] || 'us';
  }

  /**
   * 获取错误信息
   */
  private static getErrorMessage(error: AxiosError): string {
    if (error.response) {
      return `API Error: ${error.response.status} - ${error.response.statusText}`;
    } else if (error.request) {
      return 'Network connection failed. Please check your network';
    } else {
      return 'Request configuration error';
    }
  }
}

/**
 * 火山引擎AI编辑服务
 */
export class VolcengineService {
  private static async ensureSupportedImage(image: string): Promise<string> {
    if (!image) return image;
    const toPngDataUrl = (src: string): Promise<string> => new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('无法创建画布'));
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => reject(new Error('图片加载失败'));
      img.src = src;
    });

    if (image.startsWith('data:')) {
      const header = image.substring(5, image.indexOf(','));
      const headerLower = header.toLowerCase();
      if (headerLower.includes('image/png') || headerLower.includes('image/jpeg')) {
        return image;
      }
      return await toPngDataUrl(image);
    }

    if (/^https?:\/\//.test(image)) {
      try {
        const resp = await fetch(image);
        const blob = await resp.blob();
        const reader = new FileReader();
        const dataUrl: string = await new Promise((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        const headerLower = dataUrl.substring(5, dataUrl.indexOf(',')).toLowerCase();
        if (headerLower.includes('image/png') || headerLower.includes('image/jpeg')) {
          return dataUrl;
        }
        return await toPngDataUrl(dataUrl);
      } catch {
        return image;
      }
    }

    // 纯Base64字符串，补齐PNG头
    return `data:image/png;base64,${image}`;
  }
  /**
   * AI图标编辑
   */
  static async editIcon(params: AIEditParams): Promise<ApiResponse<AIEditResponse>> {
    try {
      const apiKey = getVolcengineApiKey();
      if (!apiKey) {
        return {
          success: false,
          error: 'API key not configured',
        };
      }

      const payload: any = {
        model: getSeedreamModelId(),
        prompt: params.prompt,
        image: await this.ensureSupportedImage(params.image),
        size: '1k',
        sequential_image_generation: 'disabled',
        response_format: 'b64_json',
        watermark: false,
      };

      const response = await volcengineApi.post('/api/v3/images/generations', payload, {
        headers: {
          Authorization: `Bearer ${apiKey}`,
        },
      });

      const resp = response.data;

      let imageBase64: string | undefined;
      let imageUrl: string | undefined;
      let taskId: string | undefined;

      if (resp?.data) {
        const d = resp.data;
        if (Array.isArray(d)) {
          const first = d[0] || {};
          if (first.b64_json) {
            imageBase64 = `data:image/png;base64,${first.b64_json}`;
          }
          if (first.url) {
            imageUrl = first.url;
          }
        } else {
          if (d.image_base64) {
            imageBase64 = d.image_base64;
          }
          if (d.image_url) {
            imageUrl = d.image_url;
          }
          if (d.task_id || resp.id) {
            taskId = d.task_id || resp.id;
          }
        }
      } else if (resp?.images && Array.isArray(resp.images)) {
        const first = resp.images[0] || {};
        if (first.b64_json) {
          imageBase64 = `data:image/png;base64,${first.b64_json}`;
        }
        if (first.url) {
          imageUrl = first.url;
        }
      }

      if (!imageBase64 && !imageUrl) {
        return {
          success: false,
          error: 'AI processing failed',
        };
      }

      return {
        success: true,
        data: {
          success: true,
          data: {
            imageBase64,
            imageUrl,
            taskId,
          },
        },
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      return {
        success: false,
        error: this.getErrorMessage(axiosError),
      };
    }
  }

  /**
   * 验证API密钥有效性
   */
  static async validateApiKey(): Promise<ApiResponse<boolean>> {
    try {
      const apiKey = getVolcengineApiKey();
      if (!apiKey) {
        return { success: true, data: false };
      }
      const testImage = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=='; // 1x1透明PNG
      
      const response = await this.editIcon({
        image: testImage,
        prompt: 'test',
        strength: 0.1,
        outputSize: { width: 64, height: 64 },
      });

      return {
        success: true,
        data: response.success,
      };
    } catch (error) {
      return {
        success: false,
        error: 'API key validation failed',
      };
    }
  }

  /**
   * 获取错误信息
   */
  private static getErrorMessage(error: AxiosError): string {
    if (error.response) {
      const status = error.response.status;
      const data: any = (error.response as any).data;
      let serverMsg = '';
      if (typeof data === 'string') {
        serverMsg = data;
      } else if (data) {
        const raw = data.message || data.error || data.errmsg || data.reason;
        serverMsg = typeof raw === 'string' ? raw : JSON.stringify(raw || data);
      }
      if (status === 401) {
        return 'Invalid or expired API key';
      } else if (status === 403) {
        return 'Insufficient API key permissions';
      } else if (status === 429) {
        return 'Rate limit exceeded. Please try again later';
      } else if (status >= 500) {
        return 'Server error. Please try again later';
      }
      if (status === 404) {
        return serverMsg ? `API Error 404: ${serverMsg}` : 'Endpoint or model not found. Check model ID and access';
      }
      return serverMsg ? `API Error ${status}: ${serverMsg}` : `API Error: ${status} - ${error.response.statusText}`;
    } else if (error.request) {
      return 'Network connection failed. Check local proxy or retry';
    } else {
      return 'Request configuration error';
    }
  }
}

export default {
  iTunesService: iTunesService,
  VolcengineService: VolcengineService,
};
